import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, RotateCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { FiscalReceiptStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { FiscalEventTimeline } from '@/components/fiscal/FiscalEventTimeline';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { formatPLN } from '@/utils/currency';
import { computeFiscalReceiptTotal } from '@/utils/fiscalAmounts';
import { downloadBlob } from '@/utils/fiscalDownload';
import { useFiscalApi, type FiscalEventItem, type FiscalReceiptListItem } from '@/services/api';
import { t } from '@/i18n';

const RETRYABLE_STATUSES = new Set(['REJECTED']);

interface FiscalReceiptDetailDialogProps {
  nip: string;
  uuid: string | null;
  /** printerId -> printer code, for the "drukarka" row. */
  printerCodes: Record<string, string>;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

export function FiscalReceiptDetailDialog({
  nip,
  uuid,
  printerCodes,
  onOpenChange,
  onChanged,
}: FiscalReceiptDetailDialogProps) {
  const { getReceipt, getReceiptEvents, getReceiptPdf, retryReceipt } = useFiscalApi();
  const [receipt, setReceipt] = useState<FiscalReceiptListItem | null>(null);
  const [events, setEvents] = useState<FiscalEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const [rec, ev] = await Promise.all([getReceipt(nip, uuid), getReceiptEvents(nip, uuid)]);
      setReceipt(rec);
      setEvents(ev);
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [nip, uuid, getReceipt, getReceiptEvents]);

  useEffect(() => {
    if (uuid) load();
    else {
      setReceipt(null);
      setEvents([]);
    }
  }, [uuid, load]);

  const handleDownloadPdf = async () => {
    if (!uuid || !receipt) return;
    setPdfLoading(true);
    try {
      const blob = await getReceiptPdf(nip, uuid);
      downloadBlob(blob, `${receipt.jpkid || uuid}.pdf`);
    } catch {
      toast.error(t('fiscal.receipts.detail.downloadError'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!uuid) return;
    setRetrying(true);
    try {
      const result = await retryReceipt(nip, uuid);
      if (result.ok) {
        toast.success(t('fiscal.receipts.detail.retrySuccess'));
        onChanged();
        load();
      } else {
        toast.warning(t('fiscal.receipts.detail.retryConflict'));
      }
    } catch {
      toast.error(t('fiscal.receipts.detail.retryError'));
    } finally {
      setRetrying(false);
      setRetryConfirmOpen(false);
    }
  };

  const isConfirmed = receipt?.status === 'CONFIRMED';
  const canRetry = receipt ? RETRYABLE_STATUSES.has(receipt.status) : false;
  const total = receipt ? computeFiscalReceiptTotal(receipt.receipt) : 0;
  const printerCode = receipt?.printerId ? printerCodes[receipt.printerId] : undefined;

  return (
    <>
    <Dialog open={uuid !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('fiscal.receipts.detail.title', { jpkid: receipt?.jpkid ?? '…' })}</DialogTitle>
        </DialogHeader>

        {loading && !receipt ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : receipt ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <FiscalReceiptStatusBadge status={receipt.status} />
              <Badge variant="outline" className="font-normal">
                {t(`fiscal.receipts.mode.${receipt.mode === 'paper' ? 'paper' : 'eparagon'}`)}
              </Badge>
              {receipt.confirmedAt && (
                <span className="text-muted-foreground text-xs">
                  {new Date(receipt.confirmedAt).toLocaleString('pl-PL')}
                </span>
              )}
            </div>

            {receipt.status === 'REJECTED' && receipt.error && (
              <Alert variant="destructive">
                <AlertTitle>{t('fiscal.receipts.detail.error')}</AlertTitle>
                <AlertDescription>
                  <p>{receipt.error.message}</p>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">{t('fiscal.receipts.detail.printer')}</p>
                <p>{printerCode ?? receipt.printerId ?? '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('fiscal.receipts.detail.amount')}</p>
                <p className="font-mono tabular-nums">{formatPLN(total)} PLN</p>
              </div>
              {receipt.jpkid && (
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">{t('fiscal.receipts.detail.jpkid')}</p>
                  <p className="inline-flex items-center gap-1 font-mono text-sm break-all">
                    {receipt.jpkid}
                    <CopyValueButton value={receipt.jpkid} />
                  </p>
                </div>
              )}
            </div>

            {receipt.eDocument && (
              <>
                <Separator />
                <div className="text-sm">
                  <p className="mb-1 font-medium">{t('fiscal.receipts.detail.eDocumentTitle')}</p>
                  <p>
                    <span className="text-muted-foreground">{t('fiscal.receipts.detail.eDocumentStatus')}: </span>
                    {receipt.eDocument.status}
                  </p>
                  {receipt.eDocument.code && (
                    <p>
                      <span className="text-muted-foreground">{t('fiscal.receipts.detail.eDocumentCode')}: </span>
                      {receipt.eDocument.code}
                    </p>
                  )}
                </div>
              </>
            )}

            {isConfirmed && (
              <>
                <Separator />
                <Button variant="outline" size="sm" disabled={pdfLoading} onClick={handleDownloadPdf} className="self-start">
                  {pdfLoading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  {t('fiscal.receipts.detail.downloadPdf')}
                </Button>
              </>
            )}

            <Separator />
            <FiscalEventTimeline
              events={events}
              title={t('fiscal.receipts.detail.eventsTitle')}
              emptyLabel={t('fiscal.receipts.detail.noEvents')}
            />

            {canRetry && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setRetryConfirmOpen(true)}>
                  <RotateCw className="size-4" />
                  {t('fiscal.receipts.detail.retry')}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    <AlertDialog open={retryConfirmOpen} onOpenChange={setRetryConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('fiscal.receipts.detail.retryConfirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('fiscal.receipts.detail.retryConfirmMessage')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={retrying}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction disabled={retrying} onClick={handleRetry}>
            {retrying && <Loader2 className="size-4 animate-spin" />}
            {t('fiscal.receipts.detail.retry')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
